/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { defineMessages, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import chartIconSvg from './assets/chart-icon.svg';
import databaseIconSvg from './assets/database-icon.svg';
import diagramIconSvg from './assets/diagram-icon.svg';
import remixArrowsSvg from './assets/remix-arrows.svg';
import remixGradientSvg from './assets/remix-gradient.svg';
import visualAidIconSvg from './assets/visual-aid.svg';

const messages = defineMessages({
	heading: {
		id: 'malleable-ui-components.remix.heading',
		defaultMessage: 'Create a remix',
		description: 'Heading displayed at the top of the Remix overlay panel',
	},
	subtitle: {
		id: 'malleable-ui-components.remix.subtitle',
		defaultMessage: 'Visualize this content in a new way',
		description: 'Subtitle below the Create a remix heading',
	},
	labsLozenge: {
		id: 'malleable-ui-components.remix.labs-lozenge',
		defaultMessage: 'LABS',
		description: 'Label for the LABS lozenge in the Remix overlay header',
	},
	selectedContent: {
		id: 'malleable-ui-components.remix.selected-content',
		defaultMessage: 'Selected content',
		description: 'Label shown above the displayed selected text content in the Remix overlay',
	},
	noContent: {
		id: 'malleable-ui-components.remix.no-content',
		defaultMessage: 'No content selected',
		description: 'Fallback message displayed when no text content is currently selected',
	},
	remixOptionChart: {
		id: 'malleable-ui-components.remix.option.chart',
		defaultMessage: 'Chart',
		description: 'Label for the Chart remix option',
	},
	remixOptionDiagram: {
		id: 'malleable-ui-components.remix.option.diagram',
		defaultMessage: 'Diagram',
		description: 'Label for the Diagram remix option',
	},
	remixOptionVisualAid: {
		id: 'malleable-ui-components.remix.option.visual-aid',
		defaultMessage: 'Visual aid',
		description: 'Label for the Visual aid remix option',
	},
	remixOptionDatabase: {
		id: 'malleable-ui-components.remix.option.database',
		defaultMessage: 'Database',
		description: 'Label for the Database remix option',
	},
	describePlaceholder: {
		id: 'malleable-ui-components.remix.describe-placeholder',
		defaultMessage: 'Or describe your remix...',
		description: 'Placeholder for the remix description input field',
	},
	describeSubmitLabel: {
		id: 'malleable-ui-components.remix.describe-submit-label',
		defaultMessage: 'Submit',
		description: 'Accessible label for the describe input submit button',
	},
	remixIconAlt: {
		id: 'malleable-ui-components.remix.icon-alt',
		defaultMessage: 'Remix icon',
		description: 'Alt text for the decorative remix gradient icon in the overlay header',
	},
	remixArrowsIconAlt: {
		id: 'malleable-ui-components.remix.arrows-icon-alt',
		defaultMessage: 'Remix arrows',
		description: 'Alt text for the decorative remix arrows icon in the overlay header',
	},
	chartIconAlt: {
		id: 'malleable-ui-components.remix.option.chart-icon-alt',
		defaultMessage: 'Chart icon',
		description: 'Alt text for the chart option icon',
	},
	diagramIconAlt: {
		id: 'malleable-ui-components.remix.option.diagram-icon-alt',
		defaultMessage: 'Diagram icon',
		description: 'Alt text for the diagram option icon',
	},
	visualAidIconAlt: {
		id: 'malleable-ui-components.remix.option.visual-aid-icon-alt',
		defaultMessage: 'Visual aid icon',
		description: 'Alt text for the visual aid option icon',
	},
	databaseIconAlt: {
		id: 'malleable-ui-components.remix.option.database-icon-alt',
		defaultMessage: 'Database icon',
		description: 'Alt text for the database option icon',
	},
});

const ROVO_GRADIENT_BACKGROUND = `conic-gradient(
  from var(--gradient-angle, 45deg),
  #FCA700 10%,
  #6A9A23 30%,
  #6A9A23 50%,
  #1868DB 70%,
  #AF59E0 90%
)`;

const rovoGradientRotation = keyframes({
	'0%': { '--gradient-angle': '0deg' },
	'100%': { '--gradient-angle': '360deg' },
});

const rovoGradientVisualStyle = css({
	'@property --gradient-angle': {
		syntax: '"<angle>"',
		initialValue: '270deg',
		inherits: 'false',
	},
	width: '100%',
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	background: ROVO_GRADIENT_BACKGROUND,
	animation: `${rovoGradientRotation} linear 2s infinite`,
	borderRadius: 'inherit',
});

const rovoGradientContainerStyle = css({
	width: '100%',
	flex: '1 1 auto',
	position: 'relative',
	overflow: 'visible',
	borderRadius: 'inherit',
	display: 'flex',
	flexDirection: 'column',
});

const rovoGradientInnerStyle = css({
	paddingBlock: token('space.025'),
	paddingInline: token('space.025'),
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
	backgroundClip: 'content-box',
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
});

const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

const remixStyles = cssMap({
	headerRow: {
		width: '100%',
	},
	labsRow: {
		width: '100%',
	},
	remixIconWrapper: {
		position: 'relative',
		width: '64px',
		height: '64px',
	},
	remixArrowsLayer: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	subtitle: {
		color: token('color.text.subtlest'),
		marginBlockStart: token('space.050'),
	},
	describeInputBorderWrapper: {
		width: '100%',
		marginBlockStart: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.050'),
	},
	remixOptionsStack: {
		width: '100%',
	},
	remixOptionPressable: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	remixOptionIconContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.tile'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
	},
});

const remixIconLayerStyle = css({
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	objectFit: 'cover',
});

const remixArrowsImgStyle = css({
	width: '28px',
	height: '28px',
	objectFit: 'contain',
});

const RemixOptionIcon = ({ src, alt }: { src: string; alt: string }) => (
	<Box as="span" xcss={remixStyles.remixOptionIconContainer}>
		<Image src={src} alt={alt} />
	</Box>
);

type RemixOptionKind = 'chart' | 'diagram' | 'visualAid' | 'database';

type RemixOption = {
	type: RemixOptionKind;
	icon: string;
	labelMessage: MessageDescriptor;
	iconAltMessage: MessageDescriptor;
	testId: string;
};

const REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'chart',
		icon: chartIconSvg,
		labelMessage: messages.remixOptionChart,
		iconAltMessage: messages.chartIconAlt,
		testId: 'remix-overlay-option-chart',
	},
	{
		type: 'diagram',
		icon: diagramIconSvg,
		labelMessage: messages.remixOptionDiagram,
		iconAltMessage: messages.diagramIconAlt,
		testId: 'remix-overlay-option-diagram',
	},
	{
		type: 'visualAid',
		icon: visualAidIconSvg,
		labelMessage: messages.remixOptionVisualAid,
		iconAltMessage: messages.visualAidIconAlt,
		testId: 'remix-overlay-option-visual-aid',
	},
	{
		type: 'database',
		icon: databaseIconSvg,
		labelMessage: messages.remixOptionDatabase,
		iconAltMessage: messages.databaseIconAlt,
		testId: 'remix-overlay-option-database',
	},
];

/**
 * Props for the Remix overlay modal.
 */
interface RemixOverlayProps {
	/** The text content currently selected by the user, displayed in the overlay. */
	selectedText: string;
	/** Called when the user closes the overlay (e.g. escape or overlay click). */
	onClose: () => void;
	/** Optional callback when the user selects a remix option (chart, diagram, visualAid, database). */
	onOptionSelect?: (type: RemixOptionKind) => void;
}

/**
 * A centered modal overlay that displays the currently selected content.
 * Used by the Remix feature in both the floating toolbar and block menu.
 */
export function RemixOverlay({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- will be used in a follow-up implementation
	selectedText,
	onClose,
	onOptionSelect,
}: RemixOverlayProps) {
	const { formatMessage } = useIntl();
	const [describeValue, setDescribeValue] = useState('');

	return (
		<ModalTransition>
			<Modal onClose={onClose} width={300} height={444} testId="remix-overlay">
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- @property at-rule and keyframes animation require compiled css, which is not supported by Box xcss */}
				<div css={rovoGradientVisualStyle} data-testid="remix-overlay-gradient-border">
					<div css={rovoGradientContainerStyle}>
						<div css={rovoGradientInnerStyle}>
							<div css={modalContentWrapperStyle}>
								<ModalHeader hasCloseButton={false}>
									<Box xcss={remixStyles.headerRow}>
										<Stack space="space.200" alignInline="center">
											<Flex justifyContent="end" xcss={remixStyles.labsRow}>
												<Lozenge appearance="new">
													<FormattedMessage {...messages.labsLozenge} />
												</Lozenge>
											</Flex>
											<Flex
												xcss={remixStyles.remixIconWrapper}
												aria-hidden
												alignItems="center"
												justifyContent="center"
											>
												<img
													src={remixGradientSvg}
													alt={formatMessage(messages.remixIconAlt)}
													css={remixIconLayerStyle}
												/>
												<Box xcss={remixStyles.remixArrowsLayer}>
													<img
														src={remixArrowsSvg}
														alt={formatMessage(messages.remixArrowsIconAlt)}
														css={remixArrowsImgStyle}
													/>
												</Box>
											</Flex>
											<Stack space="space.050" alignInline="center">
												<ModalTitle>
													<FormattedMessage {...messages.heading} />
												</ModalTitle>
												<Box xcss={remixStyles.subtitle}>
													<Text size="small" color="color.text.subtle">
														<FormattedMessage {...messages.subtitle} />
													</Text>
												</Box>
											</Stack>
										</Stack>
									</Box>
								</ModalHeader>
								<ModalBody hasInlinePadding={false}>
									<Box xcss={remixStyles.modalBodyPadding}>
										<Stack
											space="space.100"
											alignInline="stretch"
											xcss={remixStyles.remixOptionsStack}
										>
											{REMIX_OPTIONS.map((option) => (
												<Pressable
													key={option.type}
													xcss={remixStyles.remixOptionPressable}
													onClick={() => onOptionSelect?.(option.type)}
													testId={option.testId}
												>
													<Flex alignItems="center" gap="space.100">
														<RemixOptionIcon
															src={option.icon}
															alt={formatMessage(option.iconAltMessage)}
														/>
														<Text>{formatMessage(option.labelMessage)}</Text>
													</Flex>
												</Pressable>
											))}
										</Stack>
									</Box>
									<Box xcss={remixStyles.describeInputBorderWrapper}>
										<Box xcss={remixStyles.modalBodyPadding}>
											<Flex xcss={remixStyles.describeInputWrapper}>
												<Textfield
													appearance="none"
													placeholder={formatMessage(messages.describePlaceholder)}
													value={describeValue}
													onChange={(e) => setDescribeValue(e.currentTarget.value)}
													isCompact
													aria-label={formatMessage(messages.describePlaceholder)}
													testId="remix-overlay-describe-input"
													elemAfterInput={
														<IconButton
															appearance="primary"
															shape="circle"
															isDisabled={!describeValue?.trim()}
															icon={ArrowUpIcon}
															label={formatMessage(messages.describeSubmitLabel)}
															onClick={() => {}}
															testId="remix-overlay-describe-submit"
														/>
													}
												/>
											</Flex>
										</Box>
									</Box>
								</ModalBody>
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</ModalTransition>
	);
}
